import { useRef, useState } from "react";
import { FileDown, ExternalLink, Pencil, Trash2, ArrowUp, ArrowDown, Star, Upload, Check, X } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { fileUrl } from "../lib/products";
import { uploadAttachment, deleteAttachment, renameAttachment, formatApiError } from "../lib/adminApi";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png,.webp,.pdf";
const MAX_MB = 40;
const iconBtn = "rounded-lg p-2.5 text-[#4F5D70] hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent";

export const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel = "Elimina", onConfirm, busy, testId }) => (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent data-testid={testId}>
            <AlertDialogHeader>
                <AlertDialogTitle>{title}</AlertDialogTitle>
                {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel data-testid={`${testId}-cancel`}>Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={onConfirm} disabled={busy} data-testid={`${testId}-confirm`} className="bg-[#E30613] hover:bg-[#C20410]">
                    {busy ? "Attendere…" : confirmLabel}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// Riquadro di caricamento: click o trascina uno o più file (PDF fino a 40 MB; immagini ridimensionate a 2000px e senza EXIF dal server).
export const Dropzone = ({ product, onDone }) => {
    const inputRef = useRef(null);
    const [label, setLabel] = useState("");
    const [queue, setQueue] = useState([]); // {name, progress, error}
    const [over, setOver] = useState(false);

    const run = async (files) => {
        const list = Array.from(files || []).filter(Boolean);
        if (!list.length) return;
        const tooBig = list.filter((f) => f.size > MAX_MB * 1024 * 1024);
        if (tooBig.length) toast.error(`${tooBig.map((f) => f.name).join(", ")}: oltre ${MAX_MB} MB`);
        const ok = list.filter((f) => f.size <= MAX_MB * 1024 * 1024);
        setQueue(ok.map((f) => ({ name: f.name, progress: 0 })));
        let done = 0;
        for (let i = 0; i < ok.length; i += 1) {
            const f = ok[i];
            try {
                await uploadAttachment({ file: f, product, label: ok.length === 1 ? label : "", onProgress: (p) => setQueue((q) => q.map((x, j) => (j === i ? { ...x, progress: p } : x))) });
                done += 1;
            } catch (err) {
                const msg = formatApiError(err);
                setQueue((q) => q.map((x, j) => (j === i ? { ...x, error: msg } : x)));
                toast.error(`${f.name}: ${msg}`);
            }
        }
        if (done) toast.success(done === 1 ? `File aggiunto a ${product.name}` : `${done} file aggiunti a ${product.name}`);
        setLabel("");
        if (inputRef.current) inputRef.current.value = "";
        setTimeout(() => setQueue([]), 1500);
        onDone();
    };

    const busy = queue.some((q) => q.progress < 100 && !q.error);
    return (
        <div className="mt-4" data-testid={`upload-form-${product.key}`}>
            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); run(e.dataTransfer.files); }}
                data-testid={`upload-dropzone-${product.key}`}
                className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-7 text-center transition-colors ${over ? "border-[#E30613] bg-red-50" : "border-slate-300 bg-slate-50 hover:border-[#0B1F3A]"}`}
            >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0B1F3A] text-white"><Upload className="h-5 w-5" /></span>
                <span className="text-sm font-bold text-[#0B1F3A]">Trascina qui i file oppure tocca per sceglierli</span>
                <span className="text-xs text-[#4F5D70]">PDF (depliant, schede, listini) fino a 40 MB · foto JPG, PNG, WEBP, HEIC/iPhone (ridotte a 2000 px, senza dati EXIF/GPS) · anche più file insieme</span>
                <input ref={inputRef} type="file" multiple accept={ACCEPT} onChange={(e) => run(e.target.files)} className="hidden" data-testid={`upload-file-${product.key}`} />
            </div>
            <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Titolo (facoltativo, per un singolo file: es. Depliant 2026). Se vuoto, viene usato il nome del file."
                data-testid={`upload-label-${product.key}`}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#0B1F3A] outline-none focus:border-[#0B1F3A]"
            />
            {queue.length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid={`upload-queue-${product.key}`}>
                    {queue.map((q) => (
                        <li key={q.name} className="rounded-xl bg-white px-3 py-2 text-xs">
                            <div className="flex items-center justify-between gap-2">
                                <span className="truncate font-semibold text-[#0B1F3A]">{q.name}</span>
                                <span className={q.error ? "text-[#E30613]" : "text-[#4F5D70]"}>{q.error ? "Errore" : q.progress === 100 ? "Caricato" : `${q.progress}%`}</span>
                            </div>
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200"><div className={`h-full transition-[width] ${q.error ? "bg-[#E30613]" : "bg-emerald-500"}`} style={{ width: `${q.error ? 100 : q.progress}%` }} /></div>
                        </li>
                    ))}
                </ul>
            )}
            {busy && <p className="mt-2 text-xs text-[#4F5D70]">Caricamento in corso, non chiudere la pagina…</p>}
        </div>
    );
};

export const AttachmentRow = ({ product, a, index, total, isMain, onChanged, onMove, onSetMain }) => {
    const [editing, setEditing] = useState(false);
    const [label, setLabel] = useState(a.label);
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);

    const saveLabel = async () => {
        const next = label.trim();
        setEditing(false);
        if (!next || next === a.label) return setLabel(a.label);
        try {
            await renameAttachment(product.key, a.id, next);
            toast.success("Titolo aggiornato");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const remove = async () => {
        setBusy(true);
        try {
            await deleteAttachment(product.key, a.id);
            toast.success("File eliminato");
            setConfirm(false);
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <li className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`admin-attachment-${a.id}`}>
            <div className="flex items-start gap-3">
                {a.kind === "image" ? (
                    <img src={fileUrl(a)} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                ) : (
                    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#E30613]/10 text-[#E30613]"><FileDown className="h-6 w-6" /></span>
                )}
                <div className="min-w-0 flex-1">
                    {editing ? (
                        <div className="flex gap-1.5">
                            <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveLabel(); if (e.key === "Escape") { setLabel(a.label); setEditing(false); } }} data-testid={`admin-rename-input-${a.id}`} className="w-full rounded-lg border border-[#0B1F3A] px-3 py-2 text-sm text-[#0B1F3A] outline-none" />
                            <button type="button" onClick={saveLabel} className="rounded-lg bg-emerald-600 p-2 text-white" aria-label="Salva titolo" data-testid={`admin-rename-save-${a.id}`}><Check className="h-4 w-4" /></button>
                            <button type="button" onClick={() => { setLabel(a.label); setEditing(false); }} className="rounded-lg border border-slate-200 p-2 text-[#4F5D70]" aria-label="Annulla"><X className="h-4 w-4" /></button>
                        </div>
                    ) : (
                        <p className="break-words text-sm font-bold leading-snug text-[#0B1F3A]" data-testid={`admin-attachment-label-${a.id}`}>{a.label}</p>
                    )}
                    <p className="mt-1 break-all text-xs text-[#4F5D70]">
                        {a.kind === "pdf" ? "PDF" : "Immagine"} · {a.filename} · {fmtSize(a.size)}
                        {isMain && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800"><Star className="h-3 w-3" /> Foto principale</span>}
                    </p>
                </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-0.5 border-t border-slate-100 pt-2">
                <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className={iconBtn} aria-label="Sposta su" title="Sposta su" data-testid={`admin-move-up-${a.id}`}><ArrowUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className={iconBtn} aria-label="Sposta giù" title="Sposta giù" data-testid={`admin-move-down-${a.id}`}><ArrowDown className="h-4 w-4" /></button>
                <a href={fileUrl(a)} target="_blank" rel="noopener noreferrer" className={iconBtn} aria-label="Apri" title="Apri" data-testid={`admin-open-${a.id}`}><ExternalLink className="h-4 w-4" /></a>
                <button type="button" onClick={() => setEditing(true)} className={iconBtn} aria-label="Rinomina" title="Rinomina titolo" data-testid={`admin-rename-${a.id}`}><Pencil className="h-4 w-4" /></button>
                {a.kind === "image" && !isMain && (
                    <button type="button" onClick={() => onSetMain(a.id)} className={`${iconBtn} text-amber-600 hover:bg-amber-50`} aria-label="Usa come foto principale" title="Usa come foto principale" data-testid={`admin-set-main-${a.id}`}><Star className="h-4 w-4" /></button>
                )}
                <button type="button" onClick={() => setConfirm(true)} className="ml-auto rounded-lg p-2.5 text-[#E30613] hover:bg-red-50" aria-label="Elimina" title="Elimina" data-testid={`admin-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={`Eliminare "${a.label}"?`} description="Il file non sarà più visibile sul sito. L'operazione non può essere annullata." onConfirm={remove} busy={busy} testId={`admin-delete-dialog-${a.id}`} />
        </li>
    );
};
